// ICIC_NOTIFICATIONS_V1
// ICIC_POSTER_APP_INTEGRATION_V1
import '../../utils/systemTime';
import { useState, useEffect } from 'react';
import { App } from '@capacitor/app';
import { useNavigate, useLocation } from 'react-router-dom';
import StudentLogin from './StudentLogin';
import StudentMain from './StudentMain';
import StudentDevices from './StudentDevices';
import StudentProfile from './StudentProfile';
import StudentCourse from './StudentCourse';
import MobileStudentApp from './mobile/MobileStudentApp';
import { isNativeApp } from '../../utils/nativeApp';
import { requestStudentDataRefresh } from '../../utils/studentDataRefresh';
import {
  restoreNativeDeviceSession
} from '../../utils/nativeDeviceAuth';
import {
  restoreMasterStudentSession,
  revokeMasterStudentSession
} from '../../utils/masterStudentAccess';
import StudentCheckIn from './StudentCheckIn';
import StudentAllCourseReadings from './StudentAllCourseReadings';
import StudentHonors from './StudentHonors';
import ICICPortal from '../../components/ICICPortal';
import ICICPosterSubmission from '../../components/ICICPosterSubmission';

const NATIVE_SIGNED_OUT_KEY = 'studentNativeSignedOut';

function getStudentSession() {
  try {
    const raw = localStorage.getItem('studentSession');

    if (!raw) {
      return null;
    }

    const session = JSON.parse(raw);

    if (
      !session.expiresAt ||
      Date.now() > session.expiresAt
    ) {
      localStorage.removeItem('studentSession');
      return null;
    }

    return session;
  } catch (e) {
    return null;
  }
}

function isExplicitlySignedOut() {
  return localStorage.getItem(NATIVE_SIGNED_OUT_KEY) === 'true';
}

function setExplicitlySignedOut(value) {
  if (value) {
    localStorage.setItem(NATIVE_SIGNED_OUT_KEY, 'true');
  } else {
    localStorage.removeItem(NATIVE_SIGNED_OUT_KEY);
  }
}

export default function StudentApp() {
  const [session, setSession] = useState(null);
  const [checking, setChecking] = useState(true);
  const [nativeRestoreError, setNativeRestoreError] =
    useState('');

  const [
    nativePushTarget,
    setNativePushTarget
  ] =
    useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const path = location.pathname;
  const searchParams =
    new URLSearchParams(location.search);

  const loginIdentifier =
    searchParams.get('login') || '';

  useEffect(() => {
    localStorage.setItem(
      'veritasActiveRole',
      'student'
    );
  }, []);

  const courseMatch =
    path.match(/^\/student\/course\/([^/]+)/);

  const selectedCourseId =
    courseMatch ? courseMatch[1] : null;

  const showAllReadings =
    path === '/student/readings';

  const showDevices =
    path === '/student/devices';

  const showProfile =
    path === '/student/profile';

  const showCheckIn =
    path === '/student/checkin';

  const showChat =
    path === '/student/chat';

  const showHonors =
    path === '/student/honors';

  const showIcic =
    path === '/student/icic';

  const showIcicPoster =
    path === '/student/icic/poster';

  const showGroups =
    path === '/student/groups';

  const showSchedule =
    path === '/student/schedule';

  const showCourses =
    path === '/student/courses';

  const honorsView =
    showHonors
      ? searchParams.get(
          'view'
        )
      : null;

  const selectedChatId = showChat
    ? searchParams.get('chatId')
    : null;

useEffect(() => {
  if (!session?.studentId) {
    return;
  }

  requestStudentDataRefresh();
}, [
  location.pathname,
  location.search,
  session?.studentId
]);

  useEffect(() => {
    async function init() {
      const savedSession =
        getStudentSession();

      // Master Student has its own temporary session and is restored
      // independently from the ordinary student's saved device login.
      if (
        savedSession?.isMaster &&
        savedSession?.masterSessionToken
      ) {
        try {
          const masterSession =
            await restoreMasterStudentSession(
              savedSession
            );

          if (masterSession) {
            setSession(masterSession);
            setChecking(false);
            return;
          }
        } catch (err) {
          console.error(
            'Master Student session restore failed:',
            err
          );

          setNativeRestoreError(
            err?.message ||
              'The Master Student session could not be restored.'
          );

          setChecking(false);
          return;
        }
      }

      if (isNativeApp()) {
        // A normal app reopen should restore silently. Only an explicit
        // Sign Out leaves the app sitting at the login screen.
        if (isExplicitlySignedOut()) {
          setSession(null);
          setNativeRestoreError('');
          setChecking(false);
          return;
        }

        try {
          const nativeSession =
            await restoreNativeDeviceSession();

          setSession(nativeSession);
          setNativeRestoreError('');
        } catch (err) {
          console.error(
            'Native saved-login restore failed:',
            err
          );

          setNativeRestoreError(
            err?.message ||
              'The saved app login could not be restored.'
          );
        }

        setChecking(false);
        return;
      }

      setSession(savedSession);
      setChecking(false);
    }

    init();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const data =
      location.state
        ?.nativePushTarget;

    if (!data) {
      return;
    }

    const type =
      String(
        data.type || ''
      )
        .trim()
        .toLowerCase();

    const view =
      String(
        data.view || ''
      )
        .trim()
        .toLowerCase();

    if (
      type ===
        'course_registration' ||
      view ===
        'course_registration'
    ) {
      setNativePushTarget({
        view:
          'course_registration',

        notificationKind:
          String(
            data.notificationKind ||
            ''
          ),

        receivedAt:
          data.receivedAt ||
          Date.now()
      });

    } else if (
      type === 'icic' ||
      view === 'icic_poster'
    ) {
      setNativePushTarget({
        view:
          'icic_poster',

        notificationKind:
          String(
            data.notificationKind ||
            data.event ||
            ''
          ),

        receivedAt:
          data.receivedAt ||
          Date.now()
      });

    } else if (
      type ===
      'honors_event'
    ) {
      setNativePushTarget({
        view:
          'honors_event',

        eventId:
          data.eventId ||
          data.event_id ||
          null,

        notificationKind:
          String(
            data.notificationType ||
            data.notificationKind ||
            ''
          ),

        receivedAt:
          data.receivedAt ||
          Date.now()
      });

    } else {
      setNativePushTarget({
        view:
          'home',

        receivedAt:
          data.receivedAt ||
          Date.now()
      });
    }


    /*
     * Consume the navigation state once.
     *
     * The normalized target above remains in
     * component state after this route state is
     * cleared.
     */
    navigate(
      location.pathname +
        location.search,
      {
        replace: true,
        state: null
      }
    );
  }, [
    location.state,
    location.pathname,
    location.search,
    navigate
  ]);


  useEffect(() => {
    if (!isNativeApp()) {
      return undefined;
    }

    let listener = null;
    let cancelled = false;

    async function attachListener() {
      listener = await App.addListener(
        'appStateChange',
        async ({ isActive }) => {
          if (
            !isActive ||
            cancelled
          ) {
            return;
          }

          const savedSession =
            getStudentSession();

          if (
            savedSession?.isMaster &&
            savedSession?.masterSessionToken
          ) {
            try {
              const restoredMaster =
                await restoreMasterStudentSession(
                  savedSession
                );

              if (
                !cancelled &&
                restoredMaster
              ) {
                setSession(restoredMaster);
                setNativeRestoreError('');

                /*
                 * Returning from the background must reconcile
                 * Student App data.
                 *
                 * The refresh gate decides whether this can
                 * happen immediately or must wait until the
                 * student leaves a protected working screen.
                 */
                requestStudentDataRefresh();

                return;
              }
            } catch (err) {
              console.error(
                'Master Student session revalidation failed:',
                err
              );

              return;
            }
          }

          // Do not silently undo an explicit Sign Out when the app
          // returns from the background.
          if (isExplicitlySignedOut()) {
            return;
          }

          try {
            const restored =
              await restoreNativeDeviceSession();

            if (!cancelled) {
              setSession(restored);

              /*
               * Safe screen -> refresh now.
               * Working screen -> queue exactly one refresh.
               */
              requestStudentDataRefresh();
            }
          } catch (err) {
            console.error(
              'Native device-session revalidation failed:',
              err
            );
          }
        }
      );
    }

    attachListener();

    return () => {
      cancelled = true;

      if (listener) {
        listener.remove();
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleLogin(nextSession) {
    if (isNativeApp()) {
      setExplicitlySignedOut(false);
    }

    setNativeRestoreError('');
    setSession(nextSession);
  }

  function handleProfileUpdated(nextSession) {
    setSession(nextSession);

    if (!isNativeApp()) {
      localStorage.setItem(
        'studentSession',
        JSON.stringify(nextSession)
      );
    }
  }

  async function handleLogout() {
    const currentSession = session;

    localStorage.removeItem('studentSession');

    if (isNativeApp()) {
      // Keep the saved device credential so the student can use
      // passkey/device authentication from the login screen later.
      setExplicitlySignedOut(true);
    }

    setSession(null);
    setNativeRestoreError('');

    navigate('/', {
      replace: true
    });

    if (currentSession?.isMaster) {
      await revokeMasterStudentSession(
        currentSession
      );
    }
  }

  if (checking) {
    return null;
  }

  if (
    nativeRestoreError &&
    !session
  ) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          background: '#f7f9fb',
          padding: '24px'
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '420px',
            background: 'white',
            border: '1px solid #d7dee5',
            borderRadius: '8px',
            padding: '22px'
          }}
        >
          <h2
            style={{
              margin: '0 0 8px',
              color: '#102a43'
            }}
          >
            Saved Login Unavailable
          </h2>

          <p
            style={{
              margin: '0 0 16px',
              color: '#66788a',
              lineHeight: 1.5
            }}
          >
            {nativeRestoreError}
          </p>

          <button
            type="button"
            onClick={() =>
              window.location.reload()
            }
            style={{
              width: '100%',
              border: 0,
              borderRadius: '6px',
              padding: '11px',
              background: '#215b7d',
              color: 'white',
              fontWeight: 700
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <StudentLogin
        onLogin={handleLogin}
        initialIdentifier={
          loginIdentifier
        }
      />
    );
  }

  if (isNativeApp()) {
    return (
      <MobileStudentApp
        session={session}
        pushTarget={nativePushTarget}
        onProfileUpdated={handleProfileUpdated}
        onLogout={handleLogout}
      />
    );
  }

  if (showHonors) {
    return (
      <StudentHonors
        session={session}
        initialView={
          honorsView
        }
        onBack={() =>
          navigate('/student')
        }
      />
    );
  }

  if (showProfile) {
    return (
      <StudentProfile
        session={session}
        onSaved={handleProfileUpdated}
        onBack={() =>
          navigate('/student')
        }
      />
    );
  }

  if (showDevices) {
    return (
      <StudentDevices
        session={session}
        onBack={() =>
          navigate('/student')
        }
      />
    );
  }

  if (showCheckIn) {
    return (
      <StudentCheckIn
        session={session}
        onBack={() =>
          navigate('/student')
        }
      />
    );
  }


  if (showAllReadings) {
    return (
      <StudentAllCourseReadings
        session={session}
        onBack={() =>
          navigate('/student')
        }
      />
    );
  }

  if (showIcicPoster) {
    return (
      <ICICPosterSubmission
        session={session}
        onBack={() =>
          navigate('/student')
        }
      />
    );
  }


  if (showIcic) {
    return (
      <ICICPortal
        session={session}
        actorType="student"
        onBack={() =>
          navigate('/student')
        }
      />
    );
  }


  if (selectedCourseId) {
    return (
      <StudentCourse
        session={session}
        courseId={selectedCourseId}
        onBack={() =>
          navigate('/student')
        }
        onLogout={handleLogout}
      />
    );
  }

  return (
    <StudentMain
      session={session}
      section={showSchedule ? 'schedule' : showGroups ? 'groups' : showCourses ? 'courses' : showChat ? 'chat' : 'dashboard'}
      initialChatId={selectedChatId}
      onOpenDashboard={() => navigate('/student')}
      onOpenCourses={() =>
        navigate('/student/courses')
      }
      onOpenSchedule={() => navigate('/student/schedule')}
      onOpenIcic={() =>
        navigate('/student/icic')
      }
      onOpenIcicPoster={() =>
        navigate('/student/icic/poster')
      }
      onOpenGroups={() => navigate('/student/groups')}
      onSelectCourse={courseId =>
        navigate(
          `/student/course/${courseId}`
        )
      }
      onOpenCheckIn={() =>
        navigate('/student/checkin')
      }
      onOpenChat={chatId =>
        navigate(
          chatId
            ? '/student/chat?chatId=' +
              encodeURIComponent(
                chatId
              )
            : '/student/chat'
        )
      }
      onOpenAllReadings={() =>
        navigate('/student/readings')
      }
      onOpenDevices={() =>
        navigate('/student/devices')
      }
      onOpenProfile={() =>
        navigate('/student/profile')
      }
      onOpenHonors={view =>
        navigate(
          `/student/honors?view=${
            encodeURIComponent(
              view
            )
          }`
        )
      }
      onLogout={handleLogout}
    />
  );
}